<script lang="ts" setup>
import type { MmIntelligenceItem } from '#public-research/utils/pr.utils'
import { resolveMmLogo } from '#public-research/utils/pr.utils'
import { formatValue } from '@forgd/formatter'
import { Avatar, AvatarFallback, AvatarImage } from '@forgd/ui-kit/avatar'
import { Card } from '@forgd/ui-kit/card'
import { RankBadge } from '@forgd/ui-kit/rank-badge'

const props = defineProps<MmIntelligenceItem>()

const radarMetrics = computed(() =>
  Object.fromEntries(props.metrics.map((m, i) => [String(i), m.value])),
)
</script>

<template>
  <Card class="@container relative flex flex-col">
    <RankBadge :rank="rank" :color="accentColor" />

    <div class="flex flex-col gap-y-3 p-4 pt-5">
      <header class="flex items-center gap-2">
        <Avatar class="size-8 border border-default bg-white">
          <AvatarImage :src="resolveMmLogo(imageUrl)" :alt="name" />
          <AvatarFallback class="text-2xs">
            {{ name.slice(0, 1) }}
          </AvatarFallback>
        </Avatar>
        <div class="flex min-w-0 flex-col">
          <p class="truncate text-sm leading-none font-semibold text-primal">
            {{ name }}
          </p>
          <p v-if="activeEngagementsCount != null" class="truncate text-xs leading-4.5 text-placeholder">
            {{ formatValue(activeEngagementsCount, 'string:plural,active engagement') }}
          </p>
        </div>
      </header>

      <div class="flex items-center gap-2 border-t border-default pt-4 @[22rem]:gap-5">
        <div class="size-29.5 shrink-0 @[22rem]:size-35">
          <ChartRadar
            :metrics="radarMetrics"
            :color="accentColor"
            :fill-opacity="0.18"
            hide-labels
          />
        </div>

        <ul class="flex h-full min-w-0 flex-1 flex-col justify-between gap-1">
          <li v-for="m in metrics" :key="m.label" class="flex items-center justify-between gap-2 text-primal">
            <span class="truncate text-2xs">{{ m.label }}</span>
            <span class="shrink-0 font-mono text-xs font-medium">{{ formatValue(m.value, 'number:2') }}</span>
          </li>
        </ul>
      </div>
    </div>

    <div class="grid grid-cols-2 border-t border-default">
      <div
        v-for="(stat, i) in bottomStats"
        :key="stat.label"
        class="flex flex-col gap-1 px-4 py-3"
        :class="{
          'border-r border-default': i % 2 === 0,
          'border-t border-default': i >= 2,
        }"
      >
        <span class="text-2xs text-secondary md:text-xs">{{ stat.label }}</span>
        <div class="flex items-baseline justify-between gap-2">
          <span class="text-sm font-semibold text-primal">{{ stat.value }}</span>
          <span v-if="stat.rank" class="font-mono text-xs text-placeholder">#{{ stat.rank }}</span>
        </div>
      </div>
    </div>
  </Card>
</template>
